const createAbortError = () => {
  const error = new Error('Request aborted');
  error.name = 'AbortError';
  return error;
};

const waitForRequest = (request, signal) => {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(createAbortError());

  return new Promise((resolve, reject) => {
    const handleAbort = () => reject(createAbortError());
    signal.addEventListener('abort', handleAbort, { once: true });

    request.then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', handleAbort);
    });
  });
};

export const createRequestCache = ({
  ttlMs,
  maxEntries = 100,
}) => {
  const resolvedRequests = new Map();
  const inFlightRequests = new Map();

  const setResolvedRequest = (key, value) => {
    if (resolvedRequests.has(key)) {
      resolvedRequests.delete(key);
    } else if (resolvedRequests.size >= maxEntries) {
      const oldestKey = resolvedRequests.keys().next().value;
      resolvedRequests.delete(oldestKey);
    }

    resolvedRequests.set(key, {
      expiresAt: Date.now() + ttlMs,
      value,
    });
  };

  const getFreshRequest = (key) => {
    const cached = resolvedRequests.get(key);
    if (!cached) return { found: false, value: undefined };

    if (cached.expiresAt <= Date.now()) {
      resolvedRequests.delete(key);
      return { found: false, value: undefined };
    }

    return { found: true, value: cached.value };
  };

  const getOrCreate = (
    key,
    requestFactory,
    { forceRefresh = false, signal } = {},
  ) => {
    const inFlight = inFlightRequests.get(key);
    if (inFlight) {
      return waitForRequest(inFlight, signal);
    }

    if (!forceRefresh) {
      const cached = getFreshRequest(key);
      if (cached.found) {
        return waitForRequest(Promise.resolve(cached.value), signal);
      }
    }

    const request = Promise.resolve()
      .then(requestFactory)
      .then((value) => {
        setResolvedRequest(key, value);
        return value;
      });

    inFlightRequests.set(key, request);
    const clearInFlightRequest = () => {
      if (inFlightRequests.get(key) === request) {
        inFlightRequests.delete(key);
      }
    };
    request.then(clearInFlightRequest, clearInFlightRequest);

    return waitForRequest(request, signal);
  };

  return { getOrCreate };
};
